import { useEffect } from "react";
import type { Editor } from "tldraw";
import type { SpaceCtrlSender } from "./useSpaceCtrl";
import type { TldrawCameraVector } from "../spacesPresenceUtils";

type UseStudentCameraStreamArgs = {
  // True only when the student's editor is mounted, the space is live, and
  // the ctrl channel is ready to accept messages. The hook is a no-op until
  // all three line up — disabled / closed spaces don't broadcast camera.
  enabled: boolean;
  getEditor: () => Editor | null;
  sendCtrl: SpaceCtrlSender;
};

// How long the camera must stay still before we consider the pan/zoom "settled"
// and broadcast the new position. The teacher only uses this value to know
// where the student is roughly working — there's no live-mirror, so we don't
// need throttled streaming, just one update per stable position.
const SETTLE_DELAY_MS = 1000;

/**
 * Notifies the gateway when the student's camera lands on a new position so
 * the teacher's Student Work view can jump there on open.
 *
 * Debounced (not throttled) — every camera change resets the timer, and we
 * send exactly once after SETTLE_DELAY_MS of stillness. Continuous pan/zoom
 * therefore produces zero traffic until it stops; the teacher always gets
 * the final viewport, never the intermediate flicker.
 *
 * One initial send on mount so a teacher who opens Student Work right after
 * joining has a value to mirror without waiting for the next pan.
 *
 * Camera is intentionally not persisted anywhere — TLDraw camera is
 * session-scoped and never reaches the personal snapshot. This hook is the
 * only path that surfaces it to other clients.
 */
export function useStudentCameraStream({
  enabled,
  getEditor,
  sendCtrl,
}: UseStudentCameraStreamArgs): void {
  useEffect(() => {
    if (!enabled) return;
    const editor = getEditor();
    if (!editor) return;

    let lastSent: TldrawCameraVector | null = null;
    let pending: ReturnType<typeof setTimeout> | null = null;

    const emit = () => {
      pending = null;
      const cam = editor.getCamera();
      const next = { x: cam.x, y: cam.y, z: cam.z };
      if (
        lastSent &&
        lastSent.x === next.x &&
        lastSent.y === next.y &&
        lastSent.z === next.z
      )
        return;
      lastSent = next;
      sendCtrl({ type: "camera_update", camera: next });
    };

    // Reset the debounce timer on every change — emit fires only after the
    // camera has been still for SETTLE_DELAY_MS.
    const schedule = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(emit, SETTLE_DELAY_MS);
    };

    // Initial send so a teacher arriving immediately after joining gets a
    // value without waiting for the student to move first.
    emit();

    const unsubscribe = editor.store.listen(
      ({ changes }) => {
        const touchesCamera =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Object.values(changes.added).some(
            (r: any) => r?.typeName === "camera",
          ) ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Object.values(changes.updated).some(
            ([, next]: any) => next?.typeName === "camera",
          );
        if (touchesCamera) schedule();
      },
      { source: "all", scope: "session" },
    );

    return () => {
      if (pending) clearTimeout(pending);
      unsubscribe();
    };
  }, [enabled, getEditor, sendCtrl]);
}
